/**
 * Copy text to the clipboard on web, reporting whether it actually happened.
 *
 * `navigator.clipboard` is absent in an insecure context and *rejects* when the
 * permission is denied, so a bare `await navigator.clipboard.writeText(...)`
 * either throws an unhandled rejection or — worse, when it is guarded by an
 * `if` that simply skips — lets the caller show a "Copied!" state over an empty
 * clipboard. Callers must branch on the returned boolean rather than assume.
 *
 * Falls back to the legacy `execCommand('copy')` selection, which still works
 * in the contexts the async API refuses.
 */
export async function writeToClipboard(text: string): Promise<boolean> {
  if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      /* denied or unavailable — try the legacy path below */
    }
  }
  if (typeof document === 'undefined') return false;
  const area = document.createElement('textarea');
  // `area.select()` takes focus, and removing the textarea leaves it on
  // <body> with the document selection emptied. Both callers sit inside an
  // `aria-modal` dialog that closes on a React `onKeyDown` on its own div, so
  // focus on <body> means Escape reaches no handler (the share sheet stops
  // closing) and Tab restarts from the top of the document. The failure copy
  // also asks the user to copy the selected link by hand, which needs the
  // selection back. Snapshot both and put them back.
  const previousFocus = document.activeElement;
  const previousRanges: Range[] = [];
  const selection = document.getSelection();
  if (selection) {
    for (let i = 0; i < selection.rangeCount; i += 1) {
      const range = selection.getRangeAt(i);
      // Real selections only. While an <input>/<textarea> is focused its text
      // selection IS the user's selection, but the document selection is just
      // a collapsed caret somewhere around the field — so snapshotting and
      // restoring that would wipe the very highlight the failure copy tells
      // the user to copy by hand.
      if (range.collapsed) continue;
      previousRanges.push(range.cloneRange());
    }
  }
  try {
    area.value = text;
    area.readOnly = true;
    // Off-screen but still selectable: `display: none` makes the selection,
    // and therefore the copy, a no-op.
    area.style.position = 'fixed';
    area.style.top = '-1000px';
    area.style.opacity = '0';
    document.body.appendChild(area);
    area.select();
    area.setSelectionRange(0, text.length);
    return document.execCommand('copy');
  } catch {
    return false;
  } finally {
    area.remove();
    // Focus first: restoring it can collapse a selection, so the ranges go
    // back afterwards. `preventScroll` because the caller is a button and
    // Safari/iOS does not focus a <button> on click — `previousFocus` is then
    // whatever input the user last touched, often far up a scrolled sheet, and
    // a plain focus() scrolls the page to it out of nowhere.
    if (previousFocus instanceof HTMLElement)
      previousFocus.focus({ preventScroll: true });
    // Only when something was actually selected — a `return` here would
    // override the try block's, so this is a guard, not an early exit.
    // Clearing the live selection to restore "no selection" is what destroyed
    // a focused field's own highlight.
    if (previousRanges.length > 0) {
      const current = document.getSelection();
      if (current) {
        current.removeAllRanges();
        for (const range of previousRanges) current.addRange(range);
      }
    }
  }
}
